
import Vue, { PropType } from 'vue'
import { IGCB2Connector, ManualCommunication, SideBarSection, SidebarItem, SidebarType } from '@t/GCB2'
import * as t from '@root/src/store/chisai/GCB2/actionTypes'
import ClientCard from '@/components/chisai/GCB2/clientInfo/ClientCard.vue'
import ActivationCard from '@/components/chisai/GCB2/clientInfo/ActivationCard.vue'
import NeedToContactCard from '@/components/chisai/GCB2/clientInfo/NeedToContactCard.vue'
import VisitHistoryCard from '@/components/chisai/GCB2/clientInfo/VisitHistoryCard.vue'
import CommentHistoryCard from '../../../components/chisai/GCB2/clientInfo/CommentHistoryCard.vue'

import ActionButtons from '../../../components/chisai/GCB2/clientInfo/ActionButtons.vue'
import { useGcb2Store } from '@root/src/store/stores/gcb2/gcb2'
import { mapStores } from 'pinia'
import { Connector, Project } from '../../../types/main'
import { CHANNEL_TYPES, CONNECTOR_TYPES, TELEPHONY_CONNECTOR_TYPES } from '../../../vars/general'
import { TASKLIST_STATUSES } from '../../../vars/GCB2'
import { telephonyService } from '../../../services/telephony'
import { connect } from 'socket.io-client'
import { communicationUpdate, getClientVisitsHistoryData } from '../../../api/chisai/GCB2'
import cloneDeep from 'lodash/cloneDeep'
import config from '@root/config'

const FRESH_SEND_ERROR_MESSAGES: Record<string, string> = {
    FRESH_COMMUNICATION: 'Новая коммуникация. Проверьте ленту событий',
    FRESH_INFO_CHANGE: 'Новая информация. Обновите расчеты.',
    FRESH_CYCLE_CHANGE: 'Циклы возвращаемости клиента были изменены. Обновите расчеты',
}
export default Vue.extend({
    components: {
        ClientCard,
        ActivationCard,
        NeedToContactCard,
        VisitHistoryCard,
        ActionButtons,
        CommentHistoryCard,
    },
    props: {},
    data: () => ({
        visitHistoryLoading: false,
        expanded: [] as any[],
        TASKLIST_STATUSES,
        updateFeedTimestamp: 0 as Number,
        updateFeedButtonLoading: false as Boolean,
        visitHistoryData: [] as any[],
        updateCalculationsLoading: false as boolean,
    }),
    watch: {
        activeSidebarItem: {
            immediate: true,
            deep: true,
            async handler(newVal, oldVal) {
                if (!this.activeSidebarItem?.clientId) {
                    this.visitHistoryData = []
                    this.visitHistoryLoading = false
                    return
                }
                this.visitHistoryLoading = true
                const response = await getClientVisitsHistoryData({
                    projectId: this.project.id,
                    clientId: this.activeSidebarItem.clientId,
                })
                if (response.error) {
                    this.$store.dispatch('callNotify', 'При загрузке истории посещений произошла ошибка ', {
                        root: true,
                    })
                    this.visitHistoryLoading = false
                    return
                }
                this.visitHistoryData = response.data!
                this.gcb2Store.setVisitHistoryNeedUpdate(
                    this.project.id,
                    this.activeSidebarItem.clientId,
                    this.visitHistoryData
                )
                this.visitHistoryLoading = false
            },
        },
    },
    computed: {
        ...mapStores(useGcb2Store),
        activeManualCommunicationId(): string | null {
            return this.gcb2Store.sidebar.communicationId
        },
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },

        gcb2Connector(): IGCB2Connector {
            return this.project.connectors.find((el: Connector) => el.connectorType === CONNECTOR_TYPES.GCB2)!
        },
        megaphoneConnector(): Connector | undefined {
            return this.project.connectors.find(
                (el: Connector) => el.connectorType === CONNECTOR_TYPES.telephonyMegaphone
            )
        },
        showFeedUpdateButton(): Boolean {
            return Boolean(
                this.project.connectors.some(connector =>
                    [
                        CONNECTOR_TYPES.telephonyBeeline,
                        CONNECTOR_TYPES.telephonyMango,
                        CONNECTOR_TYPES.telephonyMegaphone,
                    ].includes(connector.connectorType)
                )
            )
        },
        activeSidebarItem(): SidebarItem | null {
            return this.gcb2Store.sidebar.item
        },
        sidebarType(): SidebarType | null {
            return this.gcb2Store.sidebar.type
        },
        activeManualCommunication(): ManualCommunication {
            const item = this.$store.getters[`GCB2/${t.GET_ONE_MANUAL_COMMUNICATION}`](
                this.activeManualCommunicationId
            )
            const updatedItem = cloneDeep(item)
            if (item && !updatedItem.activationMedia) {
                updatedItem.activationMedia = null
            }
            return (
                updatedItem ?? {
                    activation: {},
                }
            )
        },
        hasSidebarClient(): boolean {
            return Boolean(this.activeSidebarItem?.clientId)
        },
        showRecomendations(): boolean {
            return (
                this.sidebarType === 'tasklist' &&
                this.activeManualCommunication.status !== TASKLIST_STATUSES.DONT_REMIND &&
                this.activeManualCommunication.status !== TASKLIST_STATUSES.DONT_REMIND_SERVICE
            )
        },
        needUpdateVisitHistory(): boolean {
            return this.gcb2Store.needUpdateVisitHistory(
                this.project.id,
                this.activeManualCommunication.clientId
            )
        },
        showCommunicationBtns(): boolean {
            const isYcAvailable = config.botYcAvailable.some(el => window.location.pathname.includes(el))
            const hasYClientsConnector = this.project?.connectors.some(
                (el: any) => el.connectorType === CONNECTOR_TYPES.yClients
            )
            const hasWhatsappConnector = this.project?.connectors.some(
                (el: any) => el.connectorType === CONNECTOR_TYPES.whatsapp
            )
            const hasWhatsappBasisConnector = this.project?.connectors.some(
                (el: any) => el.connectorType === CONNECTOR_TYPES.whatsappBasis
            )
            const hasAltegioConnector = this.project?.connectors.some(
                (el: any) => el.connectorType === CONNECTOR_TYPES.altegio
            )
            const hasYCHrefInfo = this.activeManualCommunication?.hrefInfo?.find(
                (el: any) => el.connectorType === CONNECTOR_TYPES.yClients
            )
            const hasHrTgConnector = this.project?.connectors?.some(
                (el: any) => el.connectorType === CONNECTOR_TYPES.telegramHr
            )
            const hasTelHrefInfo = this.activeManualCommunication?.hrefInfo?.find((el: any) => el.connectorType === 'tel')
            const telephonyConnector = this.project.connectors.find((el: any) =>
                TELEPHONY_CONNECTOR_TYPES.includes(el.connectorType)
            )
            return (
                [
                    TASKLIST_STATUSES.NEED_TO_CONTACT,
                    TASKLIST_STATUSES.NEED_TO_CONTACT_TODAY,
                    TASKLIST_STATUSES.NEED_TO_CONTACT_YESTERDAY,
                    TASKLIST_STATUSES.COMMUNICATION_ERROR
                ].includes(this.activeManualCommunication.status) &&
                ((hasYClientsConnector && hasYCHrefInfo && isYcAvailable) ||
                    (hasWhatsappConnector && hasTelHrefInfo) ||
                    (hasAltegioConnector && hasTelHrefInfo) ||
                    (hasWhatsappBasisConnector && hasTelHrefInfo) ||
                    hasHrTgConnector || telephonyConnector ||
                    (this.isChannelsRework && this.isSomeChannel)
                )
            )
        },
        showCommunicationResultBtns(): boolean {
            return ![
                TASKLIST_STATUSES.IN_PROGRESS
            ].includes(this.activeManualCommunication.status)
        },
        showClientSettingsBtns(): boolean {
            return ![
                TASKLIST_STATUSES.IN_PROGRESS,
                TASKLIST_STATUSES.DONT_REMIND_SERVICE
            ].includes(this.activeManualCommunication.status)
        },
        isChannelsRework(): boolean {
            return Boolean(this.project.isChannelsRework)
        },
        isSomeChannel(): boolean {
            return this.project.connectors.some(el => {
                return Object.values(CHANNEL_TYPES).includes(el.connectorType)
            })
        },
        freshSendErrorCode(): string | null {
            if (!this.activeManualCommunication?.messageError) return null
            return this.activeManualCommunication?.extra?.lastSendErrorCode?.code || null
        },
        freshSendErrorText(): string | null {
            if (!this.freshSendErrorCode) return null
            return FRESH_SEND_ERROR_MESSAGES[this.freshSendErrorCode] || null
        },
        freshSendErrorMainText(): string | null {
            if (!this.freshSendErrorText) return null
            if (this.isFreshCommunicationError) {
                return 'Новая коммуникация.'
            }
            if (this.freshSendErrorCode === 'FRESH_INFO_CHANGE') {
                return 'Новая информация.'
            }
            if (this.freshSendErrorCode === 'FRESH_CYCLE_CHANGE') {
                return 'Циклы возвращаемости клиента были изменены.'
            }
            return this.freshSendErrorText
        },
        isFreshCommunicationError(): boolean {
            return this.freshSendErrorCode === 'FRESH_COMMUNICATION'
        }
    },
    methods: {
        async handleFreshSendCtaClick() {
            if (this.isFreshCommunicationError) {
                this.openAndScrollToFeed()
                return
            }
            if (this.updateCalculationsLoading) return
            this.updateCalculationsLoading = true
            try {
                this.$store.commit('setCommunicationUpdate', true)
                await communicationUpdate({ projectId: this.project.id })
            } finally {
                this.updateCalculationsLoading = false
            }
        },
        openAndScrollToFeed() {
            if (!this.expanded.includes(0)) {
                this.expanded = [...this.expanded, 0]
            }
            this.$nextTick(() => {
                const feedPanel = this.$refs.feedPanel as any
                const target = Array.isArray(feedPanel) ? feedPanel[0]?.$el : feedPanel?.$el
                if (target?.scrollIntoView) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        },
        async updateFeed() {
            this.updateFeedButtonLoading = true
            telephonyService
            await telephonyService.updateCallHistory(this.project.connectors)
            this.updateFeedTimestamp = Date.now()
            this.updateFeedButtonLoading = false
        },
    },

    mounted() {},
})
